import * as React from 'react';
import { Autocomplete } from 'omakase';
import { AutocompleteCloseReason } from '@material-ui/lab';
import { Autocomplete as UIAutocomplete } from 'ui';
import { getPlacesAutosuggestions } from 'utils/locationUtils';
import { isEmpty, keyBy, throttle, toString } from 'es-toolkit/compat';
import { useIsVisglMapsEnabled } from 'featureFlags/useIsVisglMapsEnabled';

export interface MainProps {
  label?: string;
  name?: string;
  onChange(place?: google.maps.places.AutocompletePrediction | null): void;
  selectedPlace?: google.maps.places.AutocompletePrediction;
  types: PlacesAutocompleteType[];
  sessionToken?: google.maps.places.AutocompleteSessionToken;
  componentRestrictions?: google.maps.places.ComponentRestrictions;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

export type OmakaseRelatedProps =
  | { useOmakaseUpdate?: false; placeholderText?: undefined }
  | { useOmakaseUpdate: true; placeholderText: string };

export type Props = MainProps & OmakaseRelatedProps;

// Supported autocomplete place types. See
// https://developers.google.com/maps/documentation/javascript/places-autocomplete
// for documentation on what these mean.
export type PlacesAutocompleteType =
  'geocode' | 'address' | 'establishment' | '(regions)' | '(cities)';

const LocationSuggestField = ({
  label,
  onChange,
  selectedPlace,
  types,
  sessionToken,
  componentRestrictions,
  error,
  helperText,
  name,
  useOmakaseUpdate = false,
  placeholderText,
  disabled,
  ...props
}: Props) => {
  // TODO(visgl-burn-in): remove the `useNewApi` flag and use only the modern
  // (vis.gl) autosuggestion data source once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  const useNewApi = useIsVisglMapsEnabled();
  const [locationSearch, setLocationSearch] = React.useState<string>('');

  const [placesOptions, setPlacesOptions] = React.useState<SelectOption[]>();
  const [placesAutosuggestionsById, setPlacesAutosuggestionsById] =
    React.useState<Dictionary<google.maps.places.AutocompletePrediction>>();

  React.useEffect(() => {
    fetchAutosuggestions(
      setPlacesAutosuggestionsById,
      setPlacesOptions,
      types,
      locationSearch,
      componentRestrictions,
      sessionToken,
      useNewApi
    );
    return () => fetchAutosuggestions.cancel();
  }, [types, locationSearch, componentRestrictions, sessionToken, useNewApi]);

  const onSelect = (event: React.ChangeEvent<{}>, value: SelectOption | null) => {
    if (value) {
      placesAutosuggestionsById &&
        placesAutosuggestionsById[value.value] &&
        onChange(placesAutosuggestionsById[value.value]);
    } else {
      onChange(null);
    }
  };

  const onSearchChange = (_event: React.ChangeEvent<{}>, newInputValue: string) => {
    if (newInputValue === selectedPlace?.description && locationSearch !== '') {
      return;
    }
    setLocationSearch(newInputValue);
  };

  const handleClearSearch = () => {
    if (selectedPlace) {
      // We don't clear location search when setting value to null - MUI's Autocomplete will handle that with an onInputChange event, reason: "clear"
      onChange(null);
      return;
    }
    setLocationSearch('');
  };

  const onClose = (event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
    // If we're selecting an option, we'll let the input "reset" handle changing locationSearch, once the new value has been passed down through props
    if (reason !== 'select-option') {
      setLocationSearch(selectedPlace?.description || '');
    }
    fetchAutosuggestions.cancel();
    setPlacesOptions(undefined);
    setPlacesAutosuggestionsById(undefined);
    hasLoadedSuccessfully = false;
  };

  const value = selectedPlace
    ? {
        key: selectedPlace.place_id,
        value: selectedPlace.place_id,
        label: selectedPlace.description,
      }
    : null;

  const optionsDontIncludeSelection =
    value !== null && !placesOptions?.find((option) => option.value === value.value);

  const options =
    placesOptions && optionsDontIncludeSelection
      ? [...placesOptions, value]
      : (placesOptions ?? []);

  const Component = useOmakaseUpdate ? Autocomplete : UIAutocomplete;

  return (
    <Component
      label={label}
      classes={{
        noOptions: hasLoadedSuccessfully ? undefined : 'LocationSuggest_options-noSearchTerm',
      }}
      getOptionSelected={(option, value) => option.value === value.value}
      options={options}
      getOptionLabel={(option: Option) => toString(option.label)}
      value={value}
      onChange={onSelect}
      inputValue={locationSearch}
      onInputChange={onSearchChange}
      TextFieldProps={{
        error,
        helperText,
        // Note that placeholderText will be defined iff useOmakaseUpdate is true,
        // due to the way we've set up the props.
        placeholder: placeholderText,
        name: name,
      }}
      onClose={onClose}
      autoSelect
      onClearSearch={useOmakaseUpdate ? handleClearSearch : undefined}
      disabled={disabled}
      {...props}
    />
  );
};

let hasLoadedSuccessfully = false;

const fetchAutosuggestions = throttle(
  async (
    setPlacesAutosuggestionsById: (
      placesById: Dictionary<google.maps.places.AutocompletePrediction>
    ) => void,
    setPlacesOptions: (options: SelectOption[]) => void,
    types: string[],
    locationSearch?: string,
    componentRestrictions?: google.maps.places.ComponentRestrictions,
    sessionToken?: google.maps.places.AutocompleteSessionToken,
    useNewApi = false
  ) => {
    if (locationSearch && !isEmpty(locationSearch)) {
      let placesAutosuggestions: google.maps.places.AutocompletePrediction[];
      try {
        placesAutosuggestions = await getPlacesAutosuggestions(
          locationSearch,
          types,
          componentRestrictions,
          sessionToken,
          useNewApi
        );
      } catch (error) {
        // This call site previously had no try/catch, so a rejection silently
        // dropped the suggestions. Surface it to the logger, then rethrow to
        // preserve the original propagation behavior.
        const cause = error instanceof Error ? error : new Error(String(error));
        window.LOGGER?.error('Failed to fetch location autosuggestions', cause);
        throw error;
      }
      const placesAutosuggestionsById = keyBy(placesAutosuggestions, 'place_id');
      const selectOptionPlaces = placesAutosuggestions.map((placesResult): SelectOption => {
        return {
          value: placesResult.place_id,
          key: placesResult.place_id,
          label: placesResult.description,
        };
      });
      setPlacesAutosuggestionsById(placesAutosuggestionsById);
      setPlacesOptions(selectOptionPlaces);
      hasLoadedSuccessfully = true;
    }
  },
  300
);

export default LocationSuggestField;
